// src/utils/backtests.ts
export type BuiltPair = {
  pair: string;
  total_profit_pct: number;
  total_profit_quote: number;
  total_fees_quote: number;
  net_profit_quote: number; // total_profit_quote - total_fees_quote
};

const toNum = (v: unknown) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const safeJson = <T = unknown>(v: any): T | undefined => {
  if (v == null) return undefined;
  if (typeof v === 'string') {
    try { return JSON.parse(v) as T; } catch { return undefined; }
  }
  return v as T;
};

/**
 * @deprecated Use buildPairsForCard from './pairs.ts' instead.
 * This function is kept for backwards compatibility only.
 *
 * Pastato porų sąrašą iš v_backtests_cards eilutės:
 * - pirmenybė pairs_stats_top10 (jei yra)
 * - fallback į pairs_tested / tested_pairs
 * - fallback į selected_coins iš backtests lentelės
 * - panaikina dublikatus
 * - paskaičiuoja neto PnL po fees
 */
export function buildPairsList(row: any): BuiltPair[] {
  // 1) Pirmiausia bandome gauti detalias metrikas (pairs_stats_top10)
  const parsedTop = safeJson<any[]>(row?.pairs_stats_top10);
  const top = Array.isArray(parsedTop)
    ? parsedTop
    : Array.isArray(row?.pairs_stats_top10)
      ? row.pairs_stats_top10
      : [];

  let out: BuiltPair[] = [];

  if (Array.isArray(top) && top.length) {
    out = top
      .map((s: any) => {
        const pair = String(s?.pair ?? s?.symbol ?? '').trim();
        if (!pair) return null;
        const profitQ = toNum(s?.total_profit_quote ?? s?.profit_quote);
        const feesQ   = toNum(s?.total_fees_quote  ?? s?.fees_quote);
        return {
          pair,
          total_profit_pct: toNum(s?.total_profit_pct ?? s?.profit_pct),
          total_profit_quote: profitQ,
          total_fees_quote: feesQ,
          net_profit_quote: profitQ - feesQ,
        } as BuiltPair;
      })
      .filter(Boolean) as BuiltPair[];
  }

  // 2) Jei nėra detalių metrikų – fallback į tested pairs
  if (!out.length) {
    const testedArr0 = safeJson<any[]>(row?.pairs_tested);
    const testedArr  = Array.isArray(testedArr0) ? testedArr0
                     : Array.isArray(row?.pairs_tested) ? row.pairs_tested
                     : [];
    const testedCsv  = typeof row?.tested_pairs === 'string'
      ? row.tested_pairs.split(',').map((s: string) => s.trim()).filter(Boolean)
      : [];

    const merged     = [...testedArr, ...testedCsv].map(String);
    const uniq       = Array.from(new Set(merged));

    if (uniq.length > 0) {
      out = uniq.map((pair) => ({
        pair,
        total_profit_pct: 0,
        total_profit_quote: 0,
        total_fees_quote: 0,
        net_profit_quote: 0,
      }));
    }
  }

  // 3) Jei vis dar nėra duomenų, bandome gauti iš selected_coins
  if (!out.length && row?.selected_coins) {
    const selectedCoins = Array.isArray(row.selected_coins) ? row.selected_coins : [];
    const quoteCurrency = row?.quote_currency || 'USDT';
    
    if (selectedCoins.length > 0) {
      out = selectedCoins.map((coin: string) => ({
        pair: `${coin}/${quoteCurrency}`,
        total_profit_pct: 0,
        total_profit_quote: 0,
        total_fees_quote: 0,
        net_profit_quote: 0,
      }));
      
      console.log(`[BUILD PAIRS] Using selected_coins fallback: ${selectedCoins.length} coins`);
    }
  }

  // 4) Paskutinis dublikatų filtras (jei būtų)
  const seen = new Set<string>();
  out = out.filter((x) => (seen.has(x.pair) ? false : (seen.add(x.pair), true)));

  return out;
}

export type BacktestCard = {
  id: string;
  title: string;
  periodDays: number | null;
  status: string;
  createdAt: string;
};

export function toCardItem(row: any): BacktestCard {
  return {
    id: row.backtest_id,
    title: row.title ?? 'Backtest',
    periodDays: row.period_days ?? null,
    status: row.status,
    createdAt: row.created_at,
  };
}

export function formatLocalTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleString();
  } catch {
    return isoString;
  }
}